import React from 'react';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AutoCreate from './AutoCreate.jsx';
import FolderPreset from './FolderPreset.jsx';
import { hrValuesAgree } from './hrAlignment.js';
import { insertHrPage, matchesStudyPdf, readReport, scanReportPdfs } from './pdfInsert.js';
import useFolderPreset from './useFolderPreset.js';

export default function PdfReportInsert({
  studyId,
  hrBytes,
  disabled,
  summaryLimits,
  prepareAlignedGraph,
  onLockChange,
  autoStudies,
  refreshAutoStudies,
  studyDirectory,
  secondaryStudyDirectory,
  onStudiesUpdate: handleStudiesUpdate,
  studyAllowed,
  autoSettings,
}) {
  const folderCache = useFolderPreset('pdf-reports');
  const { save: saveFolder, allowed: folderAllowed } = folderCache;
  const directoryRef = React.useRef(null);
  const refreshJob = React.useRef(null);
  const attempted = React.useRef(new Set());
  const [autoActive, setAutoActive] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [refreshing, setRefreshing] = React.useState(false);
  const [valueSource, setValueSource] = React.useState('');
  const [folder, setFolder] = React.useState('');
  const [reports, setReports] = React.useState([]);
  const [choice, setChoice] = React.useState('');
  const [count, setCount] = React.useState(0);
  const [page, setPage] = React.useState(2);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [pendingSummary, setPendingSummary] = React.useState(null);
  const [success, setSuccess] = React.useState('');
  React.useEffect(() => {
    onLockChange?.(busy || autoActive || Boolean(pendingSummary));
    return () => onLockChange?.(false);
  }, [busy, autoActive, pendingSummary, onLockChange]);
  React.useEffect(() => {
    if (!folderCache.preset) return;
    directoryRef.current = folderCache.preset.directory;
    setReports(folderCache.preset.entries);
    setFolder(folderCache.preset.directory.name);
  }, [folderCache.preset]);
  const hasReport = Boolean(hrBytes);
  const matches = React.useMemo(
    () => (hasReport ? reports.filter((r) => matchesStudyPdf(r.name, studyId)) : []),
    [reports, studyId, hasReport]
  );
  React.useEffect(() => {
    setChoice(matches.length === 1 ? matches[0].name : '');
    setError('');
    setSuccess('');
  }, [matches]);
  React.useEffect(() => {
    let cancelled = false;
    setCount(0);
    setPage(2);
    const report = reports.find((r) => r.name === choice);
    if (report && folderCache.allowed)
      readReport(report)
        .then((result) => {
          if (!cancelled) setCount(result.pageCount);
        })
        .catch((err) => {
          if (!cancelled) setError(err.message);
        });
    return () => {
      cancelled = true;
    };
  }, [choice, reports, folderCache.allowed]);
  const chooseFolder = async () => {
    setBusy(true);
    setError('');
    try {
      const directory = await window.showDirectoryPicker({ mode: 'read' });
      const found = await scanReportPdfs(directory);
      directoryRef.current = directory;
      attempted.current.clear();
      setFolder(directory.name);
      setReports(found);
      folderCache.save(directory, found, '');
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const refreshReports = React.useCallback(async () => {
    if (!folderAllowed || !directoryRef.current || refreshJob.current) return;
    const directory = directoryRef.current;
    setRefreshing(true);
    const job = scanReportPdfs(directory);
    refreshJob.current = job;
    try {
      const found = await job;
      if (directoryRef.current === directory) {
        const known = new Map(reports.map((report) => [report.name, report]));
        const updated = found.map((report) => known.get(report.name) || report);
        setReports(updated);
        saveFolder(directory, updated);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      refreshJob.current = null;
      setRefreshing(false);
    }
  }, [folderAllowed, reports, saveFolder]);
  React.useEffect(() => {
    if (!folderCache.allowed || !folder || !hasReport || !studyId || matches.length)
      return undefined;
    const key = `study:${studyId}`;
    if (attempted.current.has(key)) return undefined;
    attempted.current.add(key);
    refreshReports();
    return undefined;
  }, [folder, hasReport, studyId, matches.length, refreshReports, folderCache.allowed]);
  React.useEffect(() => {
    const search = query.trim().toLowerCase();
    if (
      !folderCache.allowed ||
      !folder ||
      !search ||
      reports.some((report) => report.name.toLowerCase().includes(search))
    )
      return undefined;
    const key = `search:${search}`;
    if (attempted.current.has(key)) return undefined;
    const timer = setTimeout(() => {
      attempted.current.add(key);
      refreshReports();
    }, 500);
    return () => clearTimeout(timer);
  }, [query, reports, folder, refreshReports, folderCache.allowed]);
  const saveInsert = async (job, limits = null) => {
    // Called directly by Adjust and insert, before any PDF processing awaits.
    const destination = await window.showSaveFilePicker({
      suggestedName: job.report.name.replace(/\.pdf$/i, '_HR.pdf'),
      types: [{ description: 'PDF report', accept: { 'application/pdf': ['.pdf'] } }],
    });
    if (await destination.isSameEntry(job.report.handle))
      throw new Error('Choose a new filename to preserve the original report.');
    const aligned = limits ? job.prepareAlignedGraph(limits) : null;
    const merged = await insertHrPage(
      job.source.bytes,
      aligned?.bytes || job.hrBytes,
      job.page,
      limits,
      Boolean(limits)
    );
    const writable = await destination.createWritable();
    try {
      await writable.write(merged);
      await writable.close();
    } catch (err) {
      await writable.abort().catch(() => {});
      throw err;
    }
    aligned?.commit();
    setSuccess(`Page inserted successfully at Page ${job.page}. Saved as ${destination.name}.`);
  };
  const resolveSummary = async () => {
    if (!pendingSummary || busy || !valueSource) return;
    const job = pendingSummary;
    const limits = valueSource === 'graph' ? job.limits : job.source[valueSource];
    setBusy(true);
    setError('');
    try {
      await saveInsert(job, limits);
      setPendingSummary(null);
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const insert = async () => {
    const report = reports.find((r) => r.name === choice);
    if (!report || !hrBytes || busy) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const source = await readReport(report);
      const job = { source, hrBytes, page, report, limits: summaryLimits, prepareAlignedGraph };
      if (!hrValuesAgree(source, summaryLimits)) {
        setValueSource('');
        setPendingSummary(job);
      } else await saveInsert(job);
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const locked = autoActive || folderCache.restoring || disabled || busy || Boolean(pendingSummary);
  return (
    <Box sx={{ my: 2 }}>
      <AutoCreate
        directory={folderCache.preset?.directory}
        studies={autoStudies}
        studyDirectory={studyDirectory}
        secondaryStudyDirectory={secondaryStudyDirectory}
        onStudiesUpdate={handleStudiesUpdate}
        refreshStudies={refreshAutoStudies}
        studyAllowed={studyAllowed && folderAllowed}
        settings={{ ...autoSettings, page }}
        disabled={locked}
        onActiveChange={setAutoActive}
      />
      <FolderPreset cache={folderCache} title="Preset PDF report folder" disabled={locked} />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ my: 1 }}>
        <Button
          variant="outlined"
          onClick={chooseFolder}
          disabled={locked || typeof window.showDirectoryPicker !== 'function'}
        >
          {folder ? 'Change PDF folder' : 'Select PDF folder'}
        </Button>
        <Autocomplete
          fullWidth
          size="small"
          options={reports}
          value={reports.find((r) => r.name === choice) || null}
          disabled={locked || !folder || !folderCache.allowed}
          loading={refreshing}
          getOptionLabel={(report) => report.name}
          isOptionEqualToValue={(a, b) => a.name === b.name}
          onInputChange={(event, value, reason) => {
            if (reason === 'input' || reason === 'clear') setQuery(value);
          }}
          onChange={(event, report) => {
            setChoice(report?.name || '');
            setError('');
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Would you like to insert this page into this PDF?"
              placeholder="Search PDF number or filename"
            />
          )}
        />
        <TextField
          select
          size="small"
          label="Insert at"
          value={count >= 2 ? page : ''}
          disabled={locked || count < 2}
          sx={{ minWidth: 120 }}
          onChange={(e) => setPage(Number(e.target.value))}
        >
          {Array.from({ length: Math.max(0, count - 1) }, (_, i) => (
            <MenuItem key={i + 2} value={i + 2}>
              Page {i + 2}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          onClick={insert}
          disabled={
            locked ||
            !folderCache.allowed ||
            !hrBytes ||
            !choice ||
            count < 2 ||
            typeof window.showSaveFilePicker !== 'function'
          }
        >
          Insert
        </Button>
      </Stack>
      <Typography variant="caption">
        Generate the HR report to suggest a PDF with an exact Study ID match. Insert copies the
        report footer, renumbers pages, and saves an updated copy with the HR page before the
        selected page.
      </Typography>
      {hrBytes && folder && matches.length === 0 && (
        <Alert severity="info">
          No PDF filename exactly matches Study ID {studyId || '(blank)'} in this folder.
        </Alert>
      )}
      {matches.length > 1 && (
        <Alert severity="info">
          Multiple PDFs match this Study ID. Select the intended report.
        </Alert>
      )}
      {choice && count === 1 && (
        <Alert severity="info">
          This PDF has only one page; there is no insertion position from Page 2 through its page
          count.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Dialog
        open={Boolean(pendingSummary)}
        onClose={() => {
          if (!busy) setPendingSummary(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>HR min and max do not agree</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select the values to use in all three sections. Only one lowest Min HR and one highest
            Max HR graph label will be adjusted; intermediate graph values stay unchanged.
          </DialogContentText>
          <RadioGroup
            aria-label="Choose HR values to use"
            value={valueSource}
            onChange={(event) => setValueSource(event.target.value)}
          >
            {[
              ['heartRateBox', 'Heart Rate box', pendingSummary?.source.heartRateBox],
              ['summary', 'Summary', pendingSummary?.source.summary],
              ['graph', 'Generated Graph', pendingSummary?.limits],
            ].map(([key, label, values]) => (
              <FormControlLabel
                key={key}
                value={key}
                disabled={busy || !values || values.min === null || values.max === null}
                control={<Radio />}
                label={
                  <Box sx={{ my: 1 }}>
                    <Typography>{label}</Typography>
                    <Typography>Min: {values?.min ?? 'unavailable'} BPM</Typography>
                    <Typography>Max: {values?.max ?? 'unavailable'} BPM</Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
          {pendingSummary &&
            (!pendingSummary.source.summary?.recognized ||
              !pendingSummary.source.heartRateBox?.recognized ||
              !pendingSummary.limits) && (
              <Alert severity="error">
                All three sections must be readable before insertion. Cancel and check the source
                report.
              </Alert>
            )}
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setPendingSummary(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              busy ||
              !valueSource ||
              !pendingSummary?.source.summary?.recognized ||
              !pendingSummary?.source.heartRateBox?.recognized ||
              !pendingSummary?.limits
            }
            onClick={resolveSummary}
          >
            Adjust and insert
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(success)} onClose={() => setSuccess('')}>
        <DialogTitle>Page inserted successfully</DialogTitle>
        <DialogContent>
          <DialogContentText>{success}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccess('')}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
